import { WebsitePageStatus } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';
import { useNavigate } from '@tanstack/react-router';
import { Button, Checkbox, cn, Table } from '@utima/ui';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { memo, useCallback, useMemo, useRef } from 'react';

import { Loader } from '@/components/loader/Loader';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { DocumentContentModal } from '@/features/documents/components/DocumentContentModal';
import { useKBWebsiteHierarchyLabels } from '@/features/knowledge-base/hooks/useKBWebsiteHierarchyLabels';

import type { KBContentRow, KBFindContentsPage } from './kbTypes';
import {
  useKBContentColumns,
  type KBContentColumnActions,
} from '../hooks/useKBContentColumns';
import type { useKBSelection } from '../hooks/useKBSelection';
import {
  getKBSelectionCutState,
  type KBRowActionContext,
} from '../lib/websiteHierarchy';

const kbRowCheckboxClassName =
  'border-muted-fg/60 hover:border-muted-fg/80 data-[state=unchecked]:bg-background';

const kbTableWrapperClassName =
  'relative w-full rounded-radius border border-border';

const kbTableClassName = 'w-full caption-bottom bg-background text-sm';

const kbStickyHColClassName =
  'sticky top-[var(--kb-toolbar-height,0px)] z-20 bg-background shadow-[inset_0_-1px_0_0_hsl(var(--border))]';

// Merge three arrays into a sorted flat list
export function mergeContents(
  data: KBFindContentsPage,
  contentUpdatedSort?: 'asc' | 'desc',
): KBContentRow[] {
  const rows: KBContentRow[] = [];

  // Folders first (alphabetical)
  const sortedFolders = contentUpdatedSort
    ? data.folders
    : [...data.folders].sort((a, b) => a.name.localeCompare(b.name));

  for (const folder of sortedFolders) {
    rows.push({ rowType: 'folder', id: folder.id, data: folder });
  }

  // Documents sorted by status (errors first, then processing, then done)
  const statusOrder: Record<string, number> = {
    parsingError: 0,
    embeddingError: 1,
    uploading: 2,
    ready: 3,
    parsing: 4,
    parsingAsync: 5,
    parsed: 6,
    embedding: 7,
    done: 8,
  };

  const sortedDocs = contentUpdatedSort
    ? data.documents
    : [...data.documents].sort(
        (a, b) =>
          (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99) ||
          a.name.localeCompare(b.name),
      );

  for (const doc of sortedDocs) {
    rows.push({
      rowType: 'document',
      id: doc.id,
      data: { ...doc, websitePage: data.documentWebsitePages?.[doc.id] },
    });
  }

  // WebsitePages sorted by actionability: New → Removed → Untracked → Existing
  const pageStatusOrder: Record<string, number> = {
    [WebsitePageStatus.New]: 0,
    [WebsitePageStatus.Removed]: 1,
    [WebsitePageStatus.Untracked]: 2,
    [WebsitePageStatus.Existing]: 3,
  };

  const sortedPages = contentUpdatedSort
    ? data.discoveredPages
    : [...data.discoveredPages].sort(
        (a, b) =>
          (pageStatusOrder[a.status] ?? 99) -
            (pageStatusOrder[b.status] ?? 99) || a.url.localeCompare(b.url),
      );

  for (const page of sortedPages) {
    rows.push({ rowType: 'page', id: page.id, data: page });
  }

  return rows;
}

export type KBContentTableProps = {
  rows: KBContentRow[];
  contentUpdatedSort?: 'asc' | 'desc';
  onContentUpdatedSortChange?: (direction: 'asc' | 'desc') => void;
  folderId: string | null;
  loading?: boolean;
  selection: ReturnType<typeof useKBSelection>;
  selectedDocumentId: string | null;
  onSelectDocument: (id: string | null) => void;
  currentChatId?: string;
  defaultTab?: string;
  columnActions?: KBContentColumnActions;
  folderPaths?: Record<string, string> | null;
  onNavigateToFolder?: (folderId: string) => void;
  onSearchReset?: () => void;
  canUseAutoUpdate?: boolean;
  isManagedSource?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
};

export const KBContentTable = memo(function KBContentTable({
  rows,
  contentUpdatedSort,
  onContentUpdatedSortChange,
  folderId,
  loading,
  selection,
  selectedDocumentId,
  onSelectDocument,
  currentChatId,
  defaultTab,
  columnActions,
  folderPaths,
  onNavigateToFolder,
  onSearchReset,
  canUseAutoUpdate = true,
  isManagedSource = false,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: KBContentTableProps) {
  const navigate = useNavigate();

  const resolvedColumnActions = useMemo<KBContentColumnActions | undefined>(
    () =>
      columnActions
        ? {
            ...columnActions,
            onCutItem: (id, type) => {
              selection.startCut(folderId, new Map([[id, type]]));
            },
          }
        : undefined,
    [columnActions, folderId, selection],
  );

  const columns = useKBContentColumns(resolvedColumnActions, folderPaths, {
    canUseAutoUpdate,
    isManagedSource,
    isChatMode: !!currentChatId,
  });
  const websiteHierarchyLabels = useKBWebsiteHierarchyLabels();
  const lastClickedIndex = useRef<number | null>(null);
  const ignoreNextCheckboxToggle = useRef(false);

  const rowItems = useMemo(
    () => rows.map(row => ({ id: row.id, type: row.rowType })),
    [rows],
  );

  const selectedCount = useMemo(
    () => rows.filter(row => selection.selectedItems.has(row.id)).length,
    [rows, selection.selectedItems],
  );

  const allRowsSelected = rows.length > 0 && selectedCount === rows.length;
  const someRowsSelected = selectedCount > 0 && !allRowsSelected;

  const headerCheckboxState = allRowsSelected
    ? true
    : someRowsSelected
      ? 'indeterminate'
      : false;

  const actionContext: KBRowActionContext = {
    isChatMode: !!currentChatId,
    isManagedSource,
  };

  const handleClick = useCallback(
    (event: React.MouseEvent, row: KBContentRow, index: number) => {
      if ((event.target as HTMLElement).closest('[data-kb-row-action]')) {
        return;
      }

      const isMulti = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;

      if (isShift) {
        event.preventDefault();
        window.getSelection()?.removeAllRanges();
      }

      if (isShift && lastClickedIndex.current != null) {
        const start = Math.min(lastClickedIndex.current, index);
        const end = Math.max(lastClickedIndex.current, index);
        const rangeItems = rows.slice(start, end + 1).map(r => ({
          id: r.id,
          type: r.rowType,
        }));

        selection.selectRange(rangeItems);
      } else {
        selection.toggleItem(row.id, row.rowType, isMulti);
        lastClickedIndex.current = index;
      }
    },
    [rows, selection],
  );

  const preventShiftTextSelection = useCallback((event: React.PointerEvent) => {
    if (!event.shiftKey) {
      return;
    }

    event.preventDefault();
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent, row: KBContentRow) => {
      if ((event.target as HTMLElement).closest('[data-kb-row-action]')) {
        return;
      }

      if (row.rowType === 'folder') {
        selection.clearSelection();
        onSearchReset?.();

        if (onNavigateToFolder) {
          onNavigateToFolder(row.id);
        } else {
          navigate({
            to: '/knowledge-base',
            search: { folderId: row.id },
          } as never);
        }
      } else if (row.rowType === 'document') {
        onSelectDocument(row.id);
      } else {
        window.open(row.data.url, '_blank', 'noopener,noreferrer');
      }
    },
    [navigate, selection, onSelectDocument, onNavigateToFolder, onSearchReset],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'a') {
        event.preventDefault();
        selection.selectAll(rows.map(r => ({ id: r.id, type: r.rowType })));
      }

      if (event.key === 'Escape') {
        if (selection.cutItems) {
          selection.cancelCut();
        } else {
          selection.clearSelection();
        }
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === 'x' &&
        selection.selectedItems.size > 0
      ) {
        const cutState = getKBSelectionCutState(
          rows,
          selection.selectedItems.keys(),
          actionContext,
          websiteHierarchyLabels,
        );

        if (!cutState.enabled) {
          return;
        }

        event.preventDefault();
        selection.startCut(folderId);
      }
    },
    [rows, selection, folderId, actionContext, websiteHierarchyLabels],
  );

  return (
    <>
      <div
        className='relative outline-none'
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role='grid'
      >
        <Loader visible={!!loading} />
        <div className={kbTableWrapperClassName}>
          <table className={kbTableClassName}>
            <Table.Head>
              <Table.Row>
                <Table.HCol
                  className={cn('w-10 text-center', kbStickyHColClassName)}
                  onClick={event => event.stopPropagation()}
                  onPointerDown={event => event.stopPropagation()}
                >
                  <div data-table='prevent'>
                    <Checkbox
                      size='sm'
                      aria-label='Select all rows'
                      checked={headerCheckboxState}
                      disabled={rows.length === 0}
                      onCheckedChange={() =>
                        selection.toggleSelectAll(rowItems)
                      }
                      className={kbRowCheckboxClassName}
                    />
                  </div>
                </Table.HCol>
                {columns.map(col => (
                  <Table.HCol
                    key={col.id}
                    aria-sort={
                      col.id === 'updated'
                        ? contentUpdatedSort === 'asc'
                          ? 'ascending'
                          : contentUpdatedSort === 'desc'
                            ? 'descending'
                            : 'none'
                        : undefined
                    }
                    className={cn(
                      kbStickyHColClassName,
                      col.meta?.align === 'center' && 'text-center',
                      col.meta?.align === 'right' && 'text-right',
                    )}
                    style={
                      col.meta?.size
                        ? { maxWidth: `${col.meta.size}px` }
                        : undefined
                    }
                  >
                    {col.id === 'updated' && onContentUpdatedSortChange ? (
                      <button
                        type='button'
                        className='inline-flex items-center gap-1 rounded-sm hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary'
                        onClick={() => {
                          lastClickedIndex.current = null;
                          onContentUpdatedSortChange(
                            contentUpdatedSort === 'desc' ? 'asc' : 'desc',
                          );
                        }}
                      >
                        {typeof col.header === 'string' ? col.header : ''}
                        {contentUpdatedSort === 'desc' ? (
                          <ArrowDown size={14} aria-hidden='true' />
                        ) : contentUpdatedSort === 'asc' ? (
                          <ArrowUp size={14} aria-hidden='true' />
                        ) : (
                          <ArrowUpDown size={14} aria-hidden='true' />
                        )}
                      </button>
                    ) : typeof col.header === 'string' &&
                      (col.meta as { headerTooltip?: string } | undefined)
                        ?.headerTooltip ? (
                      <SimpleTooltip
                        title={
                          (col.meta as { headerTooltip?: string })
                            .headerTooltip!
                        }
                      >
                        <span className='cursor-help border-b border-dotted border-muted-fg/50'>
                          {col.header}
                        </span>
                      </SimpleTooltip>
                    ) : typeof col.header === 'string' ? (
                      col.header
                    ) : (
                      ''
                    )}
                  </Table.HCol>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {rows.length === 0 && !loading ? (
                <Table.Row>
                  <Table.Col
                    colSpan={columns.length + 1}
                    className='py-12 text-center text-muted-fg'
                  >
                    <div className='flex flex-col items-center gap-2'>
                      <p>
                        <Trans>No items in this folder</Trans>
                      </p>
                      <p className='text-xs'>
                        <Trans>
                          Use the toolbar above to add documents, websites, or
                          create subfolders.
                        </Trans>
                      </p>
                    </div>
                  </Table.Col>
                </Table.Row>
              ) : (
                rows.map((row, index) => {
                  const isSelected = selection.selectedItems.has(row.id);
                  const isCut = selection.cutItems?.has(row.id) ?? false;

                  return (
                    <Table.Row
                      key={row.id}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-muted/30',
                        isSelected && 'bg-primary/10',
                        isCut && 'opacity-50',
                        row.rowType === 'page' && 'opacity-60',
                      )}
                      onPointerDown={preventShiftTextSelection}
                      onClick={event => handleClick(event, row, index)}
                      onDoubleClick={event => handleDoubleClick(event, row)}
                      data-table='row'
                    >
                      <Table.Col
                        className='w-10 text-center'
                        onPointerDown={event => {
                          if (event.shiftKey) {
                            ignoreNextCheckboxToggle.current = true;
                            event.preventDefault();
                            window.getSelection()?.removeAllRanges();
                          }

                          event.stopPropagation();
                        }}
                        onClick={event => {
                          event.stopPropagation();

                          if (event.shiftKey) {
                            event.preventDefault();
                            handleClick(event, row, index);
                          }
                        }}
                      >
                        <div data-table='prevent'>
                          <Checkbox
                            size='sm'
                            aria-label='Select row'
                            checked={isSelected}
                            className={kbRowCheckboxClassName}
                            onCheckedChange={(checked: boolean) => {
                              if (ignoreNextCheckboxToggle.current) {
                                ignoreNextCheckboxToggle.current = false;

                                return;
                              }

                              selection.setItemSelected(
                                row.id,
                                row.rowType,
                                checked,
                              );
                              lastClickedIndex.current = index;
                            }}
                          />
                        </div>
                      </Table.Col>
                      {columns.map(col => (
                        <Table.Col
                          key={col.id}
                          className={cn(
                            col.meta?.align === 'center' && 'text-center',
                            col.meta?.align === 'right' && 'text-right',
                          )}
                          style={
                            col.meta?.size
                              ? { maxWidth: `${col.meta.size}px` }
                              : undefined
                          }
                        >
                          {col.cell
                            ? (col.cell as Function)({
                                row: { original: row, index },
                              })
                            : null}
                        </Table.Col>
                      ))}
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </table>
        </div>
        {hasMore && onLoadMore && (
          <div className='flex justify-center border-t border-border p-4'>
            <Button
              variant='ghost'
              outline
              disabled={isLoadingMore}
              loading={isLoadingMore}
              onClick={onLoadMore}
            >
              <Trans>Load more</Trans>
            </Button>
          </div>
        )}
      </div>

      <DocumentContentModal
        currentChatId={currentChatId}
        documentId={selectedDocumentId ?? undefined}
        onClose={() => onSelectDocument(null)}
        defaultTab={defaultTab}
      />
    </>
  );
});
